import { z } from '@/utils/zod'
import { OrganizationEntitySchema } from '@/entities/Entity'
import { StorageResourceSchema } from '@/entities/common'
import {
    BaseChannelPermissionMembersSchema,
    BaseChannelPermissionMessageSchema,
    BaseChannelPermissionSurveySchema,
} from '@/portal'
import { DeepPartial } from '@/utils/types'

export const PUBLIC_INVITATION_TOKEN_LENGTH = 20

export const RoomSynchSettingsSchema = z.object({
    autoMemberTags: z.array(z.string().min(1)),
})

export type RoomSynchSettings = z.infer<typeof RoomSynchSettingsSchema>

export const RoomStatusSchema = z.enum([
    // active => default
    'active',
    // disabled => channel cannot be read by getStream client (only server side).
    // Only owners and users with "channels.remove" can see these channel (but not messages due getStream limitation)
    // This status is used before allowing the user to delete a channel permanently.
    'disabled',
])

export type RoomStatus = z.infer<typeof RoomStatusSchema>

// -- room permission
export const RoomPermissionSchema = z.object({
    edit: z.boolean(),
})
export type RoomPermission = z.infer<typeof RoomPermissionSchema>

// -- member permissions
export const RoomPermissionMembersSchema = BaseChannelPermissionMembersSchema
export type RoomPermissionMembers = z.infer<typeof RoomPermissionMembersSchema>

// -- message permissions
export const RoomPermissionMessageSchema = BaseChannelPermissionMessageSchema.extend({
    mentionAll: z.boolean(),
})
export type RoomPermissionMessage = z.infer<typeof RoomPermissionMessageSchema>

// -- survey permissions
export const RoomPermissionSurveySchema = BaseChannelPermissionSurveySchema
export type RoomPermissionSurvey = z.infer<typeof RoomPermissionSurveySchema>

// -- comment permissions
export const RoomPermissionCommentSchema = z.object({
    insert: z.boolean(),
})
export type RoomPermissionComment = z.infer<typeof RoomPermissionCommentSchema>

// -- task permissions
export const RoomPermissionTaskSchema = z.object({
    read: z.boolean(),
    insert: z.boolean(),
    edit: z.boolean(),
    remove: z.boolean(),
})
export type RoomPermissionTask = z.infer<typeof RoomPermissionTaskSchema>

// -- other permissions
export const RoomPermissionOtherSchema = z.object({
    readInvitationLink: z.boolean(),
    createInvitationLink: z.boolean(),
})
export type RoomPermissionOther = z.infer<typeof RoomPermissionOtherSchema>

export const RoomPermissionsSchema = z.object({
    room: RoomPermissionSchema,
    member: RoomPermissionMembersSchema,
    message: RoomPermissionMessageSchema,
    survey: RoomPermissionSurveySchema,
    comment: RoomPermissionCommentSchema,
    task: RoomPermissionTaskSchema,
    other: RoomPermissionOtherSchema,
})

export type RoomPermissions = z.infer<typeof RoomPermissionsSchema>

export type PartialRoomPermissions = DeepPartial<RoomPermissions>

export const RoomSchema = OrganizationEntitySchema.extend({
    name: z.string().min(1).max(70),
    description: z.string().max(250),
    emojiUnicode: z.union([z.literal(''), z.string().min(1)]),
    image: StorageResourceSchema.nullable(),
    previewImage: StorageResourceSchema.nullable(),
    status: RoomStatusSchema,
    synchSettings: RoomSynchSettingsSchema,
    publicInvitationLink: z.union([z.literal(''), z.url()]),
    publicInvitationToken: z.union([z.literal(''), z.string().length(PUBLIC_INVITATION_TOKEN_LENGTH)]),
    announcementsMode: z.boolean().default(false),
    defaultPermissions: RoomPermissionsSchema,
    topicsCount: z.number().int().nonnegative(),
})

export type Room = z.infer<typeof RoomSchema>
